// What each colour on the landing MEANS. Colour here is an encoding, not
// decoration: a hue belongs to one thing, and it is the same hue wherever that
// thing appears (a chip, a bar segment, a card's figure, the dashboard).
//
// Three families, kept apart:
//
//   brand    molten orange is Rust and the action. Two accents stand beside it:
//            ember (the white-hot end of the same pour, used for emphasis and
//            the busiest days) and quench (the cyan of steel plunged into
//            water, the complement of orange, used for what is live and
//            interactive so the page has something cool to rest the eye on).
//   data     subsystems and architectures take the dashboard's validated
//            categorical order (scripts/history/classify.mjs, src/growth/
//            growth.css). Same ids, same hues, so a reader who learns "violet is
//            the kernel core" on the landing reads the dashboard for free.
//   syscalls the ABI table's families reuse that validated order in a fixed
//            sequence of their own; they only ever appear beside their legend.
//
// The values themselves live in CSS (colour.css) so the neon hour and any
// future theme can repaint them; this module only names the slots.

// Subsystems. Order and hues mirror SYSTEMS in scripts/history/classify.mjs.
export const SYSTEMS = [
  { id: 'kernel',    label: 'Kernel core' },
  { id: 'drivers',   label: 'Drivers' },
  { id: 'arch',      label: 'Architecture' },
  { id: 'shared',    label: 'Shared runtime' },
  { id: 'kpi',       label: 'Module API' },
  { id: 'userspace', label: 'Userspace' },
  { id: 'tools',     label: 'Tooling' },
]
export const sysColor = (id) => `var(--sys-${id}, var(--ox-text-mut))`

// Architectures, as the dashboard's arch-parity chart draws them.
export const ARCHES = { x86_64: 'var(--arch-x86)', aarch64: 'var(--arch-arm)' }

// Syscall families. First match wins, so the narrow families (signals, time,
// sockets, IPC) are tested before the broad ones they would otherwise fall
// into: `kill` is a signal before it is process control, `pipe2` is IPC
// before it is a file.
export const SYSCALL_FAMILIES = [
  { id: 'signal',  label: 'Signals',   test: /^(rt_sig|rt_tgsig|sig|kill$|tkill$|tgkill$|pause$|signalfd)/ },
  { id: 'time',    label: 'Time',      test: /^(clock_|nanosleep|gettimeofday|settimeofday|time$|times$|timer_|timerfd|alarm|getitimer|setitimer|adjtimex|utime)/ },
  { id: 'net',     label: 'Sockets',   test: /^(socket|connect|accept|bind|listen|send|recv|shutdown|getsockopt|setsockopt|getsockname|getpeername)/ },
  { id: 'ipc',     label: 'IPC & sync', test: /^(pipe|futex|eventfd|epoll|poll$|ppoll|select$|pselect|shm|sem|msg|mq_|set_robust_list|get_robust_list|membarrier)/ },
  { id: 'memory',  label: 'Memory',    test: /^(mmap|munmap|mprotect|brk|mremap|msync|mincore|madvise|mlock|munlock|mbind|set_mempolicy|get_mempolicy|migrate_pages|move_pages|memfd|pkey_|process_madvise|process_mrelease|userfaultfd|remap_file_pages|process_vm_|mseal|map_shadow_stack|cachestat)/ },
  { id: 'process', label: 'Processes', test: /^(clone|fork|vfork|execve|exit|wait|getpid|getppid|gettid|[sg]et(res|re|fs|e)?[ug]id|getgroups|setgroups|setsid|getsid|setpgid|getpgid|getpgrp|prctl|arch_prctl|sched_|set_tid_address|capget|capset|setrlimit|getrlimit|prlimit|getrusage|personality|seccomp|unshare|setns|pidfd|ptrace|getpriority|setpriority|rseq|getcpu|ioprio|kcmp|landlock|restart_syscall|vhangup|acct|chroot|pivot_root)/ },
  { id: 'file',    label: 'Files & I/O', test: /^(open|read|write|close|creat|stat|fstat|lstat|newfstat|statx|statfs|fstatfs|getdents|lseek|fsync|fdatasync|sync|mount|umount|fsmount|fsopen|fsconfig|fspick|move_mount|open_tree|mount_setattr|chdir|fchdir|getcwd|mkdir|rmdir|rename|link|unlink|symlink|readlink|chmod|fchmod|chown|fchown|lchown|truncate|ftruncate|fallocate|splice|tee$|vmsplice|sendfile|copy_file_range|inotify|fanotify|access|faccessat|dup|fcntl|ioctl|flock|pread|pwrite|preadv|pwritev|mknod|umask|name_to_handle|open_by_handle|io_|[lf]?(get|set|list|remove)xattr|file_|quotactl|readahead|close_range|syncfs|fadvise|futimesat|lookup_dcookie)/ },
  { id: 'system',  label: 'System',    test: /./ },
]

// How families are listed and stacked, and which validated slot each owns.
// Fixed, never sorted by size: the validator passes this adjacency (every
// neighbouring pair separable under dichromat vision), and a re-sort would
// quietly put two unvalidated hues side by side. "System" is the remainder and
// wears a neutral, as an "Other" should.
export const FAMILY_ORDER = ['file', 'process', 'ipc', 'memory', 'time', 'net', 'signal', 'system'];

export function familyOf(name) {
  for (const family of SYSCALL_FAMILIES) if (family.test.test(name)) return family.id
  return 'system'
}
export const famColor = (id) => `var(--fam-${id}, var(--ox-text-mut))`
